import { buildHelpRequestPayload } from '../helpRequest';
import { StyleTilesSelectorView } from './SelectorView';
import { StyleTilesMessageView } from './StyleTiles';
import { parseStyleTilesOptions } from './parseOptions';
import { parseStyleTilesReply } from './parseReply';
import type { GenerativeUiDescriptor, GenerativeUiDisplayPayload } from '../types';
import type { StyleTilesOptions } from './parseOptions';
import type { StyleTilesReply } from './parseReply';

/**
 * Descriptor for the style-tiles generative-UI message type.
 *
 * Pattern for adding a new generative-UI type:
 *
 * 1. Create a new folder under generative-ui/:
 *    NewType/
 *      - NewType.tsx (the React component, receives GenerativeUiMessageViewProps<TReply>)
 *      - NewType.test.tsx (tests for the component)
 *      - parseReply.ts (the parser function, takes unknown, returns TReply | null, and the reply type)
 *      - parseReply.test.ts (tests for the parser)
 *      - descriptor.tsx (this file: exports the descriptor for this type)
 *      - index.ts (barrel; exports descriptor, MessageView, and reply type ONLY)
 *
 * 2. Add one line to registry.ts:
 *    'new-type': newTypeDescriptor,
 *
 * 3. Add one member to the GenerativeUiType union in types.ts:
 *    export type GenerativeUiType = 'style-tiles' | 'new-type';
 *
 * Folders that lack an index.ts barrel (like WizardStep) are violations and should be
 * avoided; NewType/index.ts is the only public surface — no direct imports from
 * NewType/descriptor or NewType/parseReply outside of the generative-ui system.
 */
function parseStyleTilesDisplayPayload(
    data: unknown
): GenerativeUiDisplayPayload<'style-tiles', StyleTilesReply> | null {
    const reply = parseStyleTilesReply(data);

    if (!reply) {
        return null;
    }

    return { type: 'style-tiles', data: reply };
}

export const styleTilesDescriptor: GenerativeUiDescriptor<
    'style-tiles',
    StyleTilesOptions,
    StyleTilesReply
> = {
    type: 'style-tiles',
    parseReply: parseStyleTilesReply,
    parseDisplayPayload: parseStyleTilesDisplayPayload,
    MessageView: StyleTilesMessageView,
    render: (data) => {
        const payload = parseStyleTilesDisplayPayload(data);

        if (!payload) {
            return null;
        }

        return <StyleTilesMessageView data={payload.data} />;
    },
    parseOptions: parseStyleTilesOptions,
    renderSelector: (data, onSubmit) => {
        const options = parseStyleTilesOptions(data);

        if (!options) {
            return null;
        }

        return (
            <StyleTilesSelectorView
                options={options}
                onSubmit={(reply) => onSubmit(JSON.stringify({ type: 'style-tiles', data: reply }))}
                onRequestHelp={() =>
                    onSubmit(
                        buildHelpRequestPayload(
                            'style-tiles',
                            options.options.map(({ name }) => ({ name }))
                        )
                    )
                }
            />
        );
    },
};
